// ── analyticsShared — helpers for the Employee analytics pages ──
import { useState } from "react";
import { B } from "../../../shared/theme";
import { Card, Btn } from "../../../shared/ui";

// sort caret indicator
const caret = (active, dir) => active ? (dir === "asc" ? " ▲" : " ▼") : "";

// Owners excluded by default from the Employee analytics pages (matched case-insensitively on ownerName).
export const EMP_EXCLUDED_OWNERS = ["marc kenney", "michael keenan", "eric gill", "heather rachel"];
export const isExcludedOwner = (name) => EMP_EXCLUDED_OWNERS.includes((name || "").trim().toLowerCase());

export const parseDate = (v) => { if (!v) return null; const d = new Date(v); return isNaN(d) ? null : d; };
export const ymIdx = (d) => d.getFullYear() * 12 + d.getMonth();
export const monthEndLabel = (idx) => {
  const y = Math.floor(idx / 12), m = idx % 12;
  const last = new Date(y, m + 1, 0).getDate();
  return (m + 1) + "/" + last + "/" + y;
};

const th = { padding: "7px 10px", fontSize: 11, fontWeight: 600, color: "#fff", background: B.cyan, whiteSpace: "nowrap", textAlign: "right", position: "sticky", top: 0, zIndex: 3 };
const thL = { ...th, textAlign: "left" };
const td = { padding: "6px 10px", fontSize: 12, borderBottom: "0.5px solid var(--color-border-tertiary)", textAlign: "right", fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap" };
const tdL = { ...td, textAlign: "left", fontWeight: 500 };
const SOLID = "#fff";  // opaque backing for the sticky first column
const stickyL = { position: "sticky", left: 0, background: SOLID, zIndex: 1 };

// Pivot: rows = rowKeyOf, columns = month of dateOf, value = count. Column + row totals. Sortable by any column.
// onRowClick(rowKey) + activeRow make the Sourced By rows clickable filters.
export const PivotMatrix = ({ title, items, rowKeyOf, dateOf, onRowClick, activeRow }) => {
  const [sortKey, setSortKey] = useState("total");   // "key" | "total" | month idx (number)
  const [sortDir, setSortDir] = useState("desc");
  const clickSort = (key) => {
    if (sortKey === key) setSortDir(d => d === "asc" ? "desc" : "asc");
    else { setSortKey(key); setSortDir(key === "key" ? "asc" : "desc"); }
  };

  const rowMap = new Map();
  const monthSet = new Set();
  items.forEach(it => {
    const d = parseDate(dateOf(it));
    if (!d) return;
    const idx = ymIdx(d);
    monthSet.add(idx);
    const rk = rowKeyOf(it) || "—";
    const e = rowMap.get(rk) || { key: rk, byMonth: new Map(), total: 0 };
    e.byMonth.set(idx, (e.byMonth.get(idx) || 0) + 1);
    e.total += 1;
    rowMap.set(rk, e);
  });
  const months = [...monthSet].sort((a, b) => a - b);
  const valOf = (r) => sortKey === "key" ? r.key : sortKey === "total" ? r.total : (r.byMonth.get(sortKey) || 0);
  const rows = [...rowMap.values()].sort((a, b) => {
    const av = valOf(a), bv = valOf(b);
    let cmp;
    if (sortKey === "key") cmp = String(av).localeCompare(String(bv));
    else cmp = (av - bv) || a.key.localeCompare(b.key);
    return sortDir === "asc" ? cmp : -cmp;
  });
  const colTotal = (idx) => rows.reduce((s, r) => s + (r.byMonth.get(idx) || 0), 0);
  const grand = rows.reduce((s, r) => s + r.total, 0);
  const hcell = (key, style, label) => <th style={{ ...style, cursor: "pointer" }} onClick={() => clickSort(key)}>{label}{caret(sortKey === key, sortDir)}</th>;

  return (
    <Card style={{ padding: 0, marginBottom: 16 }}>
      <div style={{ fontSize: 11, fontWeight: 700, color: B.cyanDark, textTransform: "uppercase", letterSpacing: "0.06em", padding: "12px 14px 8px" }}>{title}</div>
      <div style={{ overflowX: "auto", maxHeight: 340 }}>
        <table style={{ borderCollapse: "collapse", whiteSpace: "nowrap", minWidth: "100%" }}>
          <thead><tr>
            {hcell("key", { ...thL, ...stickyL, background: B.cyan, zIndex: 4 }, "Sourced By")}
            {months.map(m => hcell(m, th, monthEndLabel(m)))}
            {hcell("total", { ...th, background: B.cyanDark }, "Total")}
          </tr></thead>
          <tbody>{rows.length === 0 ? <tr><td style={tdL} colSpan={months.length + 2}>No activity in range.</td></tr> : rows.map(r => {
            const active = activeRow != null && activeRow === r.key;
            return (
            <tr key={r.key} onClick={onRowClick ? () => onRowClick(r.key) : undefined} style={{ cursor: onRowClick ? "pointer" : "default" }}
              title={onRowClick ? "Click to filter the page by this employee" : undefined}>
              <td style={{ ...tdL, ...stickyL, background: active ? B.cyanLight : SOLID, fontWeight: active ? 700 : 500 }}>{r.key}</td>
              {months.map(m => <td key={m} style={{ ...td, background: active ? B.cyanLight : "" }}>{r.byMonth.get(m) || ""}</td>)}
              <td style={{ ...td, fontWeight: 700, background: active ? B.cyanLight : "" }}>{r.total}</td>
            </tr>
            );
          })}</tbody>
          <tfoot><tr style={{ background: "var(--color-background-secondary)" }}>
            <td style={{ ...tdL, ...stickyL, fontWeight: 700, background: SOLID, borderTop: "1.5px solid var(--color-border-secondary)" }}>Total</td>
            {months.map(m => <td key={m} style={{ ...td, fontWeight: 700, borderTop: "1.5px solid var(--color-border-secondary)" }}>{colTotal(m) || ""}</td>)}
            <td style={{ ...td, fontWeight: 700, borderTop: "1.5px solid var(--color-border-secondary)" }}>{grand}</td>
          </tr></tfoot>
        </table>
      </div>
    </Card>
  );
};

// KPI stat card (big number)
export const KpiCard = ({ label, value, sub }) => (
  <Card style={{ padding: 16 }}>
    <div style={{ fontSize: 11, fontWeight: 700, color: B.cyanDark, textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 10, paddingBottom: 6, borderBottom: "1.5px solid " + B.cyanLight }}>{label}</div>
    <div style={{ fontSize: 28, fontWeight: 700, lineHeight: 1 }}>{value}</div>
    {sub != null && <div style={{ fontSize: 11, color: "var(--color-text-secondary)", marginTop: 6 }}>{sub}</div>}
  </Card>
);

export const pctLabel = (num, den) => den > 0 ? (Math.round((num / den) * 1000) / 10) + "%" : "—";

// Sourced By multi-select. value is a Set (or null = default: all owners except the excluded four).
export const OwnerMultiSelect = ({ owners, value, onChange }) => {
  const [open, setOpen] = useState(false);
  const toggle = (o) => { const n = new Set(value || []); n.has(o) ? n.delete(o) : n.add(o); onChange(n); };
  return (
    <div style={{ position: "relative" }}>
      <Btn onClick={() => setOpen(v => !v)}>Sourced By{value ? " (" + value.size + ")" : ": All"} ▾</Btn>
      {open && (
        <div style={{ position: "absolute", zIndex: 20, top: "100%", left: 0, marginTop: 4, background: "#fff", border: "1px solid var(--color-border-secondary)", borderRadius: 8, boxShadow: "0 8px 24px rgba(0,0,0,0.18)", padding: 8, maxHeight: 320, overflowY: "auto", minWidth: 210 }}>
          <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 6, gap: 8 }}>
            <button onClick={() => onChange(new Set(owners))} style={{ fontSize: 11, background: "none", border: "none", color: B.cyan, cursor: "pointer" }}>Select all</button>
            <button onClick={() => onChange(new Set())} style={{ fontSize: 11, background: "none", border: "none", color: B.cyan, cursor: "pointer" }}>Clear</button>
            <button onClick={() => setOpen(false)} style={{ fontSize: 11, background: "none", border: "none", color: "var(--color-text-secondary)", cursor: "pointer" }}>Close</button>
          </div>
          {owners.map(o => (
            <label key={o} style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, padding: "3px 2px", cursor: "pointer", whiteSpace: "nowrap" }}>
              <input type="checkbox" checked={value ? value.has(o) : !isExcludedOwner(o)} onChange={() => toggle(o)} /> {o}
            </label>
          ))}
        </div>
      )}
    </div>
  );
};
